/**
 * VAT-rates settings page — ``/settings/vat-rates``.
 *
 * Same shape as the Pfand-values page (shared ``<ManagedListTable>`` +
 * numeric ``extraColumns`` field), but the value is a ``percent`` and the
 * two seeded rows ("Standard" 19%, "Reduced" 7%) are **system** rows:
 * renameable and percent-editable, but their Archive button is disabled
 * (the server 403s deactivate/delete).
 *
 * Articles reference a rate by id, so editing a rate's percent here
 * propagates to every article using it — no per-article rewrite.
 */

import { useEffect, useState } from "react";
import {
  Form,
  Input,
  Modal,
  Typography,
  notification,
} from "antd";
import { NumInput as InputNumber } from "../../components/NumInput";
import type { ColumnsType } from "antd/es/table";

import { ApiError, type VatRateRow, api } from "../../api/client";
import { ManagedListTable } from "../../components/settings/ManagedListTable";

const { Text } = Typography;

/** "19%" / "7.5%" — drop the trailing ".00" the wire may carry. */
export const formatPercent = (percent: number): string =>
  `${Number(percent)}%`;

interface FormValues {
  name: string;
  percent: number;
}

export const VatRatesPage = () => {
  const [rows, setRows] = useState<VatRateRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<VatRateRow | null>(null);
  const [form] = Form.useForm<FormValues>();

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await api.listVatRates(true));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ name: "", percent: 19 });
    setModalOpen(true);
  };

  const openEdit = (row: VatRateRow) => {
    setEditing(row);
    form.resetFields();
    form.setFieldsValue({ name: row.name, percent: row.percent });
    setModalOpen(true);
  };

  const onSubmit = async () => {
    let values: FormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setBusy(true);
    try {
      if (editing) {
        const payload: { name?: string; percent?: number } = {};
        if (values.name.trim() !== editing.name) {
          payload.name = values.name.trim();
        }
        if (values.percent !== editing.percent) {
          payload.percent = values.percent;
        }
        if (Object.keys(payload).length > 0) {
          await api.patchVatRate(editing.id, payload, editing.updated_at);
        }
      } else {
        await api.createVatRate({
          name: values.name.trim(),
          percent: values.percent,
          sort_order: rows.length * 10,
        });
      }
      setModalOpen(false);
      setEditing(null);
      await refresh();
    } catch (err) {
      notification.error({
        message: editing ? "Update failed" : "Create failed",
        description: err instanceof ApiError ? err.message : String(err),
      });
    } finally {
      setBusy(false);
    }
  };

  const onArchive = async (row: VatRateRow) => {
    setBusy(true);
    try {
      await api.deleteVatRate(row.id);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const onRestore = async (row: VatRateRow) => {
    setBusy(true);
    try {
      await api.patchVatRate(row.id, { active: true }, row.updated_at);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const onReorder = async (orderedIds: number[]) => {
    try {
      await api.reorderVatRates(orderedIds);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
      await refresh();
    }
  };

  const extraColumns: ColumnsType<VatRateRow> = [
    {
      title: "Rate",
      key: "percent",
      width: 110,
      align: "right",
      render: (_: unknown, row: VatRateRow) => (
        <Text>{formatPercent(row.percent)}</Text>
      ),
    },
  ];

  return (
    <>
      <ManagedListTable<VatRateRow>
        title="VAT rates"
        description="Named VAT rates (name + percent) for article classification. Prices stay gross; editing a rate's percent propagates to every article using it. Standard/Reduced are system rates (can't be archived)."
        rows={rows}
        loading={loading}
        busy={busy}
        error={error}
        onErrorDismiss={() => setError(null)}
        addLabel="+ New VAT rate"
        labelOf={(r) => r.name}
        keyOf={(r) => r.id}
        isArchived={(r) => !r.active}
        isSystem={(r) => r.system_seeded}
        systemArchiveTooltip="System VAT rates back the article default and can't be archived (rename + percent are editable)."
        extraColumns={extraColumns}
        onReorder={onReorder}
        onCreate={openCreate}
        onEdit={openEdit}
        onArchive={onArchive}
        onRestore={onRestore}
      />
      <Modal
        title={editing ? "Edit VAT rate" : "New VAT rate"}
        open={modalOpen}
        onOk={() => void onSubmit()}
        onCancel={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        okText={editing ? "Save" : "Create"}
        confirmLoading={busy}
        destroyOnHidden
      >
        <Form<FormValues> form={form} layout="vertical">
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: "Name is required" }]}
          >
            <Input maxLength={64} placeholder="e.g. Standard, Alcohol" autoFocus />
          </Form.Item>
          <Form.Item
            label="Percent"
            name="percent"
            rules={[
              { required: true, message: "Percent is required" },
              {
                validator: (_, v) =>
                  Number(v) >= 0
                    ? Promise.resolve()
                    : Promise.reject(new Error("must be ≥ 0")),
              },
            ]}
            tooltip="The VAT percentage, e.g. 19 or 7."
          >
            <InputNumber
              min={0}
              max={100}
              step={0.1}
              addonAfter="%"
              style={{ width: "100%" }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default VatRatesPage;
